/**
 * ClientShellFrame: the display half of the client tab shell (Home / Bookings / Invite / Profile),
 * shared by the Mini App's ClientShell and the studio website's phone tab layout
 * (shared-client-screens D3, rows S1 to S8). Props only: each surface reads its own session and
 * dashboard and hands in the status, the header slot, the bar and the page.
 *
 *  - The header: a page's own (the header slot), else on Home the studio's header mode (the logo
 *    lockup, the wordmark or the greeting), else the tab's plain title; a placeholder until ready.
 *  - The scrollable <main> that clears the floating nav, a placeholder until ready, back at the
 *    top whenever `scrollKey` changes.
 *  - The browser keyboard/viewport rails and the document scroll-lock; the bar hides while the
 *    keyboard is open.
 *  - A failed read takes the whole screen with its title and message, no bar.
 */
import { useLayoutEffect, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { useClientLock } from '@/hooks/useClientLock';
import { useIsKeyboardOpen } from '@/hooks/useIsKeyboardOpen';
import { useKeyboardAwareScroll } from '@/hooks/useKeyboardAwareScroll';
import { usePreventNativeKeyboardScroll } from '@/hooks/usePreventNativeKeyboardScroll';
import { useAppViewportCssVars } from '@/hooks/useVisualViewportCssVars';
import type { BookingHubDashboard } from '@/types/dashboard';
import { ClientNameHeader } from './ClientNameHeader';
import { ClientLogoLockupHeader } from './ClientLogoLockupHeader';
import styles from './ClientShell.module.css';

export type ClientShellStatus = 'loading' | 'ready' | { title: string; message: string };

export interface ClientShellFrameProps {
  /** loading (placeholders), ready, or the failed read's title and message. */
  status: ClientShellStatus;
  /** The dashboard, for Home's header mode, the studio's name and the greeting; undefined for a page that reads none. */
  dashboard: BookingHubDashboard | undefined;
  isHome: boolean;
  /** A page's own header; null draws Home's header or the tab title. */
  headerSlot: ReactNode | null;
  /** The plain title of a tab that draws no header of its own. */
  tabTitle?: string;
  /** The scrollable <main>, owned by the surface so it can scroll it to the top. */
  contentRef: RefObject<HTMLElement>;
  /** The bottom bar (BottomNavView). */
  nav: ReactNode;
  /** The scroller returns to the top each time this changes (a route, an in-page view). */
  scrollKey: string;
  children: ReactNode;
}

/**
 * rd-expiry W2.2 — the old ShellError "Try Again" button called
 * `window.location.reload()`, which reloads into a possibly-DEAD session (a
 * full-page reload that re-runs bootstrap against the same already-spent
 * credential — the exact eviction Gabriel's law forbids). It is removed: a
 * recoverable hub-401 now self-heals invisibly inside `apiFetch`, and a TERMINAL
 * double-expiry surfaces the Frame-5 "Close app" takeover (the one honest exit).
 * This surface stays as an honest, calm error message with NO reload dead-end.
 */
function ShellError({ title, message }: { title: string; message: string }) {
  return (
    <div className={styles.takeover} data-testid="client-shell-error">
      <h2 className={styles.takeoverTitle}>{title}</h2>
      <p className={styles.takeoverMessage}>{message}</p>
    </div>
  );
}

export function ClientShellFrame({
  status,
  dashboard,
  isHome,
  headerSlot,
  tabTitle,
  contentRef,
  nav,
  scrollKey,
  children,
}: ClientShellFrameProps) {
  const isKeyboardOpen = useIsKeyboardOpen();

  // WKWebView keyboard/viewport rails — mandatory for a layout outside AdminLayout.
  useAppViewportCssVars();
  usePreventNativeKeyboardScroll();
  useKeyboardAwareScroll(contentRef);
  // Harden the document against iOS scroll-on-focus (the shell scrolls in <main>).
  useClientLock();

  // The <main> scroller is PERSISTENT — the frame stays mounted across tab/route
  // changes (only the page swaps), so without an explicit reset the previous page's
  // scroll offset leaks into the next one (useLayoutEffect = before paint, no jump).
  useLayoutEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [contentRef, scrollKey]);

  // ── Terminal takeover (no nav) ────────────────────────────────────────
  if (typeof status === 'object') return <ShellError title={status.title} message={status.message} />;

  const ready = status === 'ready';
  // Home's header reads the dashboard; a page drawn without one (the website's Invite for a visitor) is never Home.
  const homeReady = ready && isHome && !!dashboard;
  const business = dashboard?.business;
  const useLogoLockup = homeReady && headerSlot == null && business?.headerMode === 'LOGO' && !!business.logoUrl;
  // NAME mode, or LOGO mode without a logo: the text wordmark sits just below Telegram's chrome row.
  const useWordmark =
    homeReady && headerSlot == null && (business?.headerMode === 'NAME' || (business?.headerMode === 'LOGO' && !business.logoUrl));

  return (
    <div className={styles.layout} data-testid="client-shell" data-home={isHome ? 'true' : undefined}>
      <div className={styles.atmosphere} aria-hidden="true" />

      <header
        className={`${styles.header} ${useLogoLockup ? styles.lockupHeader : isHome ? styles.homeHeader : ''} ${
          useWordmark ? styles.wordmarkHeader : ''
        } ${
          headerSlot != null ? styles.headerWithSlot : ''
        }`}
      >
        {headerSlot != null ? (
          headerSlot
        ) : homeReady || (ready && !isHome) ? (
          isHome ? (
            useLogoLockup ? (
              <ClientLogoLockupHeader
                name={dashboard!.business.name}
                logoUrl={dashboard!.business.logoUrl!}
                descriptor={dashboard!.business.descriptor}
                contentRef={contentRef}
              />
            ) : (
              <ClientNameHeader business={dashboard!.business} />
            )
          ) : (
            <h1 className={styles.tabTitle}>{tabTitle ?? ''}</h1>
          )
        ) : (
          <div className={styles.headerSkeleton} data-testid="client-shell-header-skeleton" />
        )}
      </header>

      <main
        ref={contentRef}
        className={`${styles.content} ${useLogoLockup ? styles.lockupContent : isHome ? styles.homeContent : ''}`}
        style={useLogoLockup && dashboard!.business.descriptor ? ({ '--lockup-height': '116px' } as CSSProperties) : undefined}
      >
        {ready ? (
          children
        ) : (
          <div className={styles.contentSkeleton} data-testid="client-shell-skeleton">
            <div className={styles.spinner} />
          </div>
        )}
      </main>

      {!isKeyboardOpen && nav}
    </div>
  );
}
